/**
 * Type definitions for the TRX PayPage Fields JS library
 * (`paypage-fields.trxservices.{net|com}/bundle.js`).
 *
 * The library attaches `PayPageJS` to `window` after the script loads.
 * Browsers don't ship types for it, so we declare the surface we
 * actually consume — every method/property here is documented in
 * §11 of `docs/TRX_API_REFERENCE.md`. Any drift between this file and
 * the live library shape will surface as a runtime undefined-deref;
 * keep the types in lockstep with §11 when TRX publishes updates.
 *
 * Field-container IDs (§11.3 strict whitelist): `card-number`,
 * `expiration-date`, `cvv`, `routing`, `account-number`, `card-fee`,
 * `ach-fee`, `apple-pay`, `google-pay`. We expose all of them on the
 * field-config map but only mount the subset the parent component
 * actually needs (card vs. ACH).
 */

export interface TrxPayPageSessionData {
    readonly clientId: string;
    readonly sessionId: string;
    readonly payPageId: string;
}

/** Per-field config passed in `fields` to `PayPageJS.initialize`. */
export interface TrxPayPageFieldConfig {
    /** DOM element id where the per-field iframe mounts. */
    readonly containerId: string;
    /**
     * Stylesheet for the iframe interior. Arbitrary CSS — this is the
     * "ghosted iframe equivalent" knob: we ship a stylesheet that
     * makes the iframe content visually indistinguishable from
     * surrounding host-DOM inputs.
     */
    readonly stylesCSS?: string;
    /** Wallet field option (apple-pay / google-pay only). */
    readonly onPaymentResult?: (result: TrxPayPageWalletResult) => void;
    /** Wallet button styling (apple-pay / google-pay only). */
    readonly buttonConfig?: Record<string, unknown>;
}

/** Map of field type → config. Card vs. ACH callers fill different keys. */
export interface TrxPayPageFieldsMap {
    readonly 'card-number'?: TrxPayPageFieldConfig;
    readonly 'expiration-date'?: TrxPayPageFieldConfig;
    readonly 'cvv'?: TrxPayPageFieldConfig;
    readonly 'routing'?: TrxPayPageFieldConfig;
    readonly 'account-number'?: TrxPayPageFieldConfig;
    readonly 'card-fee'?: TrxPayPageFieldConfig;
    readonly 'ach-fee'?: TrxPayPageFieldConfig;
    readonly 'apple-pay'?: TrxPayPageFieldConfig;
    readonly 'google-pay'?: TrxPayPageFieldConfig;
}

export interface TrxPayPageInitOptions {
    readonly sessionData: TrxPayPageSessionData;
    /** Return unencrypted BIN on `getEncryptedCardData()` (card/wallet only). */
    readonly includeBIN?: boolean;
    /** Return routing + DDA-last-four on `getEncryptedAchData()` (ACH only). */
    readonly includeACHInfo?: boolean;
    /**
     * Live-updated card brand callback. REQUIRED on every initialize, bank-only
     * fields included: TRX's bundle records "Set a valid 'onCardTypeChange'
     * callback function" otherwise (observed on UAT 2026-10-13).
     */
    readonly onCardTypeChange: (cardType: string) => void;
    /** Required for wallet fields. */
    readonly onPaymentResult?: (result: TrxPayPageWalletResult) => void;
    readonly fields: TrxPayPageFieldsMap;
}

/** Per §11.6: card encryption result. */
export interface TrxPayPageCardEncryptedResult {
    readonly isSuccess: boolean;
    readonly errors?: readonly TrxPayPageError[];
    readonly Encrypted?: {
        readonly Data: string;
        readonly KeyId: string;
        readonly BIN?: string;
    };
}

/** Per §11.6: ACH encryption result. */
export interface TrxPayPageAchEncryptedResult {
    readonly isSuccess: boolean;
    readonly errors?: readonly TrxPayPageError[];
    readonly Encrypted?: {
        readonly Data: string;
        readonly KeyId: string;
        readonly RoutingNumber?: string;
        readonly AccountNumberLastFour?: string;
    };
}

/** Per §11.7: wallet payment result. */
export interface TrxPayPageWalletResult {
    readonly status: 'SUCCESS' | 'FAILED' | 'CANCELLED';
    readonly message?: string;
    readonly data?: {
        readonly keyId: string;
        readonly data: string;
        readonly bin: string;
        readonly cardSecurity: {
            readonly dsrpCryptogram: string | null;
            readonly cavv: string | null;
        } | null;
    };
}

/**
 * Per §11.9: error taxonomy from `result.errors[]` and instance.errors.
 *
 * TWO SHAPES, BOTH REAL. §11.9 documents the error TYPES but never the field
 * NAMES, and the bundle emits `errorType` / `errorText` — not `type` /
 * `message`. Verbatim from prod (2026-07-30):
 *
 *   {errorType: "permissions-error", errorText: "Payment with apple pay is not allowed"}
 *
 * Reading only `type`/`message` yielded undefined for every real error, so the
 * wallet-vs-card partition never fired and an optional Apple Pay permission
 * error blocked the required card form. Every reader MUST accept both spellings
 * — use the `trxErrorType` / `trxErrorMessage` accessors rather than reaching
 * for a field directly.
 */
export interface TrxPayPageError {
    readonly type?:
        | 'wrong-config'
        | 'wrong-field'
        | 'permissions-error'
        | 'server-error'
        | 'container-error'
        | 'iframe-error'
        | 'styles-error'
        | string;
    readonly message?: string;
    readonly field?: string;
    /** Alternate spelling emitted by the live bundle. */
    readonly errorType?: string;
    /** Alternate spelling emitted by the live bundle. */
    readonly errorText?: string;
}

/** Error type across both spellings. Empty string when absent. */
export const trxErrorType = (err: TrxPayPageError): string =>
    err.type ?? err.errorType ?? '';

/** Human-readable error text across both spellings. Empty string when absent. */
export const trxErrorMessage = (err: TrxPayPageError): string =>
    err.message ?? err.errorText ?? '';

/** Returned instance from `PayPageJS.initialize(...)`. */
export interface TrxPayPageInstance {
    /** Get encrypted card payload + (optional) BIN. Deactivates fields. */
    getEncryptedCardData(): Promise<TrxPayPageCardEncryptedResult>;
    /** Get encrypted ACH payload + (optional) routing/last-four. Deactivates fields. */
    getEncryptedAchData(): Promise<TrxPayPageAchEncryptedResult>;
    /** Re-activate card fields after a failed extract for retry. */
    enableCardDataFields(): void;
    /** Re-activate ACH fields after a failed extract for retry. */
    enableAchDataFields(): void;
    /** Re-activate Apple Pay button after a wallet flow. */
    enableApplePayBtn(): void;
    /** Re-activate Google Pay button after a wallet flow. */
    enableGooglePayBtn(): void;
    /** Tear down all iframes + listeners. Call on unmount. */
    destroy(): void;
    /** Live-updated card brand string. */
    readonly cardType?: string;
    /** Init-time errors. */
    readonly errors?: readonly TrxPayPageError[];
}

/** The global the library attaches to `window` after script load. */
export interface TrxPayPageGlobal {
    initialize(options: TrxPayPageInitOptions): Promise<TrxPayPageInstance>;
}

declare global {
    interface Window {
        PayPageJS?: TrxPayPageGlobal;
    }
}

export {};
